import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, School, BarChart3, Bell, MessageSquare, Users, TrendingUp, Shield, Clock, FileText, AlertCircle, CheckCircle, Eye, Calendar, Award, Zap, Gift, ChefHat, Building2 } from 'lucide-react';
import Header from '../components/Header';
import '../styles/index.css';

const PageSchools = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "EcoLunch - Écoles | Cockpit Pilotage"; }, []);

  return (
    <div className="home-container">
      <Header currentPage="schools" lang="fr" />

      {/* Hero Schools */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <School size={16} />
            <span>Dashboard Écoles</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <span style={{ color: 'var(--actor-schools)' }}>Écoles</span>
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.3rem', lineHeight: '1.8' }}>
            Visibilité <strong style={{ color: 'var(--actor-schools)' }}>100%</strong> en temps réel<br />
            Reporting automatisé • Communication simplifiée
          </p>
          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Visibilité temps réel</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">0</div>
              <div className="stat-label">Saisie manuelle</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">∞</div>
              <div className="stat-label">Insights automatisés</div>
            </div>
          </div>
        </div>
      </section>

      {/* Manifeste — Pourquoi EcoLunch pour les Écoles */}
      <section className="section" data-testid="manifeste-schools">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Pourquoi EcoLunch pour les Écoles ?</h2>
          </div>
          <div className="manifeste-tile" data-testid="manifeste-gratuit-schools">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#3B82F6'}}><Gift size={30} /></div>
              <h3>Gratuit pour les écoles. Gratuit pour les parents. Durable pour l'écosystème.</h3>
            </div>
            <p>EcoLunch sera et restera gratuit pour les écoles. Mieux encore, notre modèle permet également le versement de dons, de sorte qu'une école peut non seulement offrir un service structuré, moderne et sans friction, mais aussi en retirer une retombée positive.</p>
            <p>Pour les parents, l'utilisation de la plateforme est également gratuite. L'objectif est clair : rendre l'accès au service {' '}<strong>simple, fluide, centralisé et sans barrières inutiles</strong>.</p>
          </div>
        </div>
      </section>

      {/* Fonctionnalités Écoles */}
      <section className="section problem-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Fonctionnalités Écoles</h2>
            <p className="section-subtitle">Tous les outils pour piloter efficacement votre alimentation scolaire</p>
          </div>
          <div className="benefits-grid-4col">
            <div className="benefit-card">
              <div className="benefit-icon"><Eye size={32} /></div>
              <h3>Visibilité en temps réel</h3>
              <p>Voyez exactement combien d'élèves ont commandé, qui mange quoi, et les tendances alimentaires instantanément.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><BarChart3 size={32} /></div>
              <h3>Reporting automatisé</h3>
              <p>Rapports de participation, satisfaction, et conformité générés automatiquement. Fini les fichiers Excel.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><MessageSquare size={32} /></div>
              <h3>Communication centralisée</h3>
              <p>Canal direct avec traiteurs, parents, et directions. Tout au même endroit, traçable et documenté.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><AlertCircle size={32} /></div>
              <h3>Gestion des incidents</h3>
              <p>Système de tickets intégré pour gérer rapidement les problèmes : repas manquants, allergies, qualité.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><Clock size={32} /></div>
              <h3>Temps de gestion réduit</h3>
              <p>Éliminez la gestion manuelle chronophage. Communications dispersées, suivi fragmenté : EcoLunch centralise et automatise tout pour vous faire gagner des heures chaque semaine.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><TrendingUp size={32} /></div>
              <h3>Amélioration continue</h3>
              <p>Identifiez les tendances et opportunités d'amélioration grâce aux données en temps réel et recommandations automatiques.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section features-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Fonctionnalités du Dashboard</h2>
            <p className="section-subtitle">
              Tous les outils pour piloter efficacement
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><BarChart3 size={32} /></div>
              <h3>Dashboard temps réel</h3>
              <p>Vue d'ensemble instantanée : taux de participation, commandes du jour, tendances hebdomadaires. Tout mis à jour en direct.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Suivi par élève</h3>
              <p>Historique de commandes pour chaque élève. Détectez les changements de comportement alimentaire rapidement.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><TrendingUp size={32} /></div>
              <h3>Analytics de satisfaction</h3>
              <p>Notes et commentaires des élèves agrégés. Identifiez les menus populaires et les points d'amélioration.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Calendar size={32} /></div>
              <h3>Planification des menus</h3>
              <p>Visualisez les menus à venir. Collaborez avec le traiteur pour ajuster selon les préférences des élèves.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Rapports automatiques</h3>
              <p>Rapports mensuels générés automatiquement : participation, revenus, satisfaction. Exportables PDF/Excel.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Bell size={32} /></div>
              <h3>Alertes intelligentes</h3>
              <p>Notifications en cas de baisse de participation, problème qualité, ou incident. Réagissez rapidement.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><MessageSquare size={32} /></div>
              <h3>Communication multi-acteurs</h3>
              <p>Messagerie intégrée avec traiteurs, parents, et directions. Historique complet des échanges.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Gestion des allergies</h3>
              <p>Liste complète des élèves avec restrictions alimentaires. Alertes automatiques en cas de risque.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Award size={32} /></div>
              <h3>Suivi de conformité</h3>
              <p>Assurez la conformité avec les normes nutritionnelles. Rapports de conformité générés automatiquement.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Export et rapports</h3>
              <p>Exportez vos données en CSV/PDF et créez des rapports personnalisés pour vos besoins spécifiques.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Communication Section */}
      <section className="section coparent-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Communication simplifiée</h2>
            <p className="section-subtitle">
              Un hub central pour toutes vos communications
            </p>
          </div>

          <div className="benefits-grid-4col">
            <div className="benefit-card">
              <h4><ChefHat size={16} style={{display:'inline', verticalAlign:'middle', color:'#84CC16'}} /> Canal Traiteur</h4>
              <p>Communication directe avec votre traiteur pour ajustements de menus, problèmes de qualité, ou changements de volume.</p>
            </div>

            <div className="benefit-card">
              <h4><Users size={16} style={{display:'inline', verticalAlign:'middle', color:'#84CC16'}} /> Canal Parents</h4>
              <p>Répondez aux questions des parents, partagez des annonces sur les menus, et gérez les demandes spéciales.</p>
            </div>

            <div className="benefit-card">
              <h4><Building2 size={16} style={{display:'inline', verticalAlign:'middle', color:'#84CC16'}} /> Canal Direction</h4>
              <p>Remontez les informations importantes à votre direction d'établissement ou commission scolaire.</p>
            </div>

            <div className="benefit-card">
              <h4><FileText size={16} style={{display:'inline', verticalAlign:'middle', color:'#84CC16'}} /> Système de tickets</h4>
              <p>Gérez les incidents (repas manquants, problèmes qualité) avec un système de tickets structuré et traçable.</p>
            </div>
          </div>
        </div>
      </section>

      {/* EcoPulse CC Mini-Card */}
      <section className="section" data-testid="ecopulse-mini-schools">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="ecopulse-mini-card" style={{borderColor: 'rgba(34, 197, 94, 0.4)'}}>
            <div className="ecopulse-mini-header">
              <div className="portal-icon" style={{color: '#84CC16'}}>
                <Zap size={28} />
              </div>
              <div>
                <h3>EcoPulse CC™ <span style={{fontSize: '0.85rem', fontWeight: 500, opacity: 0.7}}>pour les Écoles</span></h3>
                <p style={{fontSize: '0.9rem', color: 'var(--color-text-secondary)', margin: 0}}>
                  Une phrase suffit pour tout contrôler
                </p>
              </div>
            </div>
            <div className="ecopulse-mini-commands">
              <div className="ecopulse-mini-cmd">Combien d'élèves ont commandé aujourd'hui ?</div>
              <div className="ecopulse-mini-cmd">Taux de participation de ma 3e année</div>
              <div className="ecopulse-mini-cmd">Élèves allergiques aux arachides</div>
              <div className="ecopulse-mini-cmd">Rapport fréquentation pour le conseil</div>
            </div>
            <button onClick={() => navigate('/ecopulse')} className="ecopulse-mini-link">
              Découvrir toutes les commandes EcoPulse <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                La plateforme unifiée pour moderniser les repas scolaires au Québec
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/caterers')}>Pour les Traiteurs</a>
                <a onClick={() => navigate('/parents')}>Pour les Parents</a>
                <a onClick={() => navigate('/schools')}>Pour les Écoles</a>
                <a onClick={() => navigate('/directions')}>Pour les Directions</a>
                <a onClick={() => navigate('/css')}>Pour les CSS</a>
              </div>

              <div className="footer-column">
                <h4>Plateforme</h4>
                <a onClick={() => navigate('/platform')}>Architecture</a>
                <a onClick={() => navigate('/coparentalite')}>Co-Parentalité</a>
                <a onClick={() => navigate('/ecopulse')}>EcoPulse CC</a>
                <a onClick={() => navigate('/ecoloop')}>EcoLoop</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-legal">
              <span>© 2025 EcoLunch. Tous droits réservés.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PageSchools;